import { useState } from 'react';
import StorageService from '../services/storageService';
import { KEYS } from '../services/initService';
import useReveal from '../hooks/useReveal';

function ReviewCard({ review }) {
  return (
    <div style={{
      background: 'var(--cream)',
      borderRadius: 'var(--radius)',
      padding: '1.25rem',
      borderLeft: '3px solid var(--teal)',
      marginBottom: '1rem',
    }}>
      <div style={{ color: 'var(--gold)', fontSize: '1.05rem', letterSpacing: 2, marginBottom: '.5rem' }}>
        {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
      </div>
      <p style={{ fontSize: '.9rem', color: 'var(--dark)', lineHeight: 1.65, marginBottom: '.75rem' }}>
        "{review.text}"
      </p>
      <div style={{ fontSize: '.78rem', color: 'var(--gray)', fontWeight: 600 }}>
        {review.name}, {new Date(review.date).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
      </div>
    </div>
  );
}

export default function FeedbackSection({ reviews, onReviewAdded }) {
  const revealRef = useReveal();
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [form, setForm] = useState({ name: '', contact: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.message.trim()) {
      setError('Please enter your name and message.');
      return;
    }
    setSubmitting(true);
    await StorageService.push(KEYS.REVIEWS, {
      id: 'rv' + Date.now(),
      name: form.name.trim(),
      contact: form.contact.trim(),
      rating,
      text: form.message.trim(),
      date: new Date().toISOString(),
    });
    setSubmitting(false);
    setSubmitted(true);
    if (onReviewAdded) onReviewAdded();
  };

  return (
    <section id="feedback" className="section" style={{ background: 'var(--white)' }}>
      <div className="section-inner">
        <div className="reveal">
          <span className="section-label">Guest Reviews</span>
          <h2 className="section-title">What Our Guests Say</h2>
        </div>

        <div
          ref={revealRef}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '4rem',
            marginTop: '2.5rem',
            alignItems: 'start',
          }}
          className="feedback-grid reveal"
        >
          {/* Reviews list */}
          <div>
            {reviews.length === 0 ? (
              <div style={{
                background: 'var(--cream)',
                borderRadius: 'var(--radius)',
                padding: '2rem',
                border: '1px dashed var(--border)',
                color: 'var(--gray)',
                fontSize: '.95rem',
                lineHeight: 1.65,
              }}
              >
                <p style={{ margin: 0, fontWeight: 600, color: 'var(--dark)', marginBottom: '.5rem' }}>Be the first to share your experience</p>
                <p style={{ margin: 0 }}>We are building our wall of guest love. Leave a review on the right and it will appear here for everyone.</p>
              </div>
            ) : (
              reviews.slice(0, 6).map((r) => (
                <ReviewCard key={r.id} review={r} />
              ))
            )}
          </div>

          {/* Feedback form */}
          <div
            style={{
              background: 'var(--teal-pale)',
              borderRadius: 'var(--radius-lg)',
              padding: '2rem',
              border: '1px solid var(--border)',
              animationDelay: '.15s',
            }}
          >
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--dark)', marginBottom: '1.25rem' }}>
              Leave a Review
            </h3>

            {submitted ? (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🙏</div>
                <p style={{ fontWeight: 600, color: 'var(--dark)', marginBottom: '.5rem' }}>Thank you for your review!</p>
                <p style={{ color: 'var(--gray)', fontSize: '.9rem' }}>Your feedback means everything to us.</p>
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label className="form-label">Your Rating</label>
                  <div style={{ display: 'flex', gap: '.4rem', marginBottom: '.25rem' }}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        onClick={() => setRating(n)}
                        onMouseEnter={() => setHoverRating(n)}
                        onMouseLeave={() => setHoverRating(0)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '1.6rem',
                          color: n <= (hoverRating || rating) ? 'var(--gold)' : '#ddd',
                          transition: 'color .15s, transform .15s',
                          transform: n <= (hoverRating || rating) ? 'scale(1.15)' : 'scale(1)',
                          padding: 0,
                        }}
                        aria-label={`Rate ${n} star${n !== 1 ? 's' : ''}`}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="fb-name">Your Name</label>
                  <input
                    id="fb-name"
                    className="form-input"
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="fb-contact">Phone or Email (optional)</label>
                  <input
                    id="fb-contact"
                    className="form-input"
                    placeholder="For follow-up if needed"
                    value={form.contact}
                    onChange={(e) => setForm({ ...form, contact: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="fb-message">Your Message</label>
                  <textarea
                    id="fb-message"
                    className="form-textarea"
                    placeholder="Tell us about your experience..."
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                  />
                </div>

                {error && (
                  <p style={{ color: 'var(--red)', fontSize: '.82rem', marginBottom: '.75rem' }}>{error}</p>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{
                    width: '100%',
                    background: submitting ? 'var(--gray-light)' : 'var(--teal-dark)',
                    color: 'white',
                    border: 'none',
                    padding: '.85rem',
                    borderRadius: 'var(--radius-pill)',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 700,
                    fontSize: '.95rem',
                    transition: 'all .2s',
                  }}
                >
                  {submitting ? 'Submitting...' : 'Submit Review'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .feedback-grid { grid-template-columns: 1fr !important; gap: 2rem !important; }
        }
      `}</style>
    </section>
  );
}
